import React from 'react';
import { useLocation } from 'react-router-dom';
import { SkeletonBlock, SkeletonCard, SkeletonRows } from './Skeleton.jsx';

// What a tab shows while its CODE is still downloading (2026-08-11).
//
// Tom tapped Profile and sat on a centred "Loading…" for a long time. The
// natural suspicion was the read budget — and that was worth fixing — but it
// was not this. Every screen in this app ALREADY renders a proper skeleton
// while its data loads; you just never saw it, because React.lazy's Suspense
// fallback replaces the whole page while the route CHUNK downloads, and that
// fallback was one line of grey text.
//
// So the wait had two halves with two different personalities: text for the
// JS, skeletons for the data — with a full re-layout at the seam. Now the
// chunk wait wears the same shape the screen is about to render, so the two
// halves read as one continuous load and nothing jumps when the seam passes.
//
// Shapes are matched to each destination deliberately. A generic skeleton
// would still flash-and-reflow; the point is that the placeholder is already
// standing where the content will be.
export default function RouteFallback() {
  const { pathname } = useLocation();
  const p = pathname || '';

  if (p.startsWith('/profile')) {
    return (
      <main className="ps-page" aria-busy="true" aria-label="Loading your profile">
        <SkeletonBlock w={96} h={96} r="50%" style={{ margin: '4px auto 16px' }} />
        <SkeletonBlock w="46%" h={20} style={{ margin: '0 auto 24px' }} />
        <SkeletonCard lines={3} />
        <SkeletonCard lines={2} />
      </main>
    );
  }
  if (p.startsWith('/stats')) {
    return (
      <main className="ps-page" aria-busy="true" aria-label="Loading your stats">
        <SkeletonBlock w={140} h={140} r="50%" style={{ margin: '8px auto 22px' }} />
        <SkeletonCard lines={3} />
        <SkeletonCard lines={2} />
      </main>
    );
  }
  if (p.startsWith('/results')) {
    return (
      <main className="ps-page" aria-busy="true" aria-label="Loading your results">
        <SkeletonBlock w="62%" h={36} r={999} style={{ margin: '0 auto 18px' }} />
        <SkeletonCard lines={2} />
        <SkeletonCard lines={2} />
        <SkeletonCard lines={2} />
      </main>
    );
  }
  if (p.startsWith('/groups')) {
    return (
      <main className="ps-page" aria-busy="true" aria-label="Loading your games">
        <SkeletonBlock w="100%" h={132} mb={16} />
        <SkeletonRows rows={5} />
      </main>
    );
  }
  if (p.startsWith('/leaderboard') || p.startsWith('/coin-ledger') || p.startsWith('/creators')) {
    return (
      <main className="ps-page" aria-busy="true" aria-label="Loading">
        <SkeletonBlock w="52%" h={22} style={{ margin: '0 auto 20px' }} />
        <SkeletonRows rows={6} />
      </main>
    );
  }
  // The invite doors (/i, /f, /challenge) own their own anticipation state
  // once mounted (QuestionIncoming). Their chunk wait gets the quietest
  // possible placeholder rather than a competing one.
  return (
    <main className="ps-page" aria-busy="true" aria-label="Loading">
      <SkeletonCard image lines={2} />
    </main>
  );
}
